import { defaultCTA } from 'src/modules/utils/constants'
import { COMMON_FEATURES, PlanIntervals } from '.'
import { ratePlanCodes } from '../ratePlanCode'
import { channelIds } from 'src/modules/hooks/useChannelSelector'
import { calculateLowestPrice, createNetworkLogo, modifyListItems } from 'src/modules/utils/plans'
import React from 'react'
import { promoCodes } from '../promoCodes'
import { networkLogos } from '../networkLogos'

//base pricing for each plan in canada
export const caPlanPricing = {
  entertainment: {
    [PlanIntervals.monthly]: { price: 17.99, discountedPrice: null },
    [PlanIntervals.quarterly]: { price: 50.97, discountedPrice: null },
  },
  sports: {
    [PlanIntervals.monthly]: { price: 31.49, discountedPrice: 26.99 },
    [PlanIntervals.quarterly]: { price: 83.97, discountedPrice: 44.97 },
    [PlanIntervals.annual]: { price: 293.99, discountedPrice: 230.99 },
  },
  premium: {
    [PlanIntervals.monthly]: { price: 45.14, discountedPrice: null },
    [PlanIntervals.quarterly]: { price: 110.23, discountedPrice: null },
    [PlanIntervals.annual]: { price: 415.67, discountedPrice: null },
  },
  cricket: {
    [PlanIntervals.monthly]: { price: 14.99, discountedPrice: null },
    [PlanIntervals.quarterly]: { price: 35.97, discountedPrice: null },
    [PlanIntervals.annual]: { price: 119.89, discountedPrice: null },
  },
  cricket_w_sports_plus: {
    [PlanIntervals.monthly]: { price: 9.99, discountedPrice: null },
    [PlanIntervals.quarterly]: { price: 26.96, discountedPrice: null },
    [PlanIntervals.annual]: { price: 89.93, discountedPrice: null },
  },
}

//Each plan requires channelId, price, plan
const baseData = {
  entertainment: {
    planType: 'entertainment',
    channelId: channelIds.ca_entertainment,
    promoImg:
      'https://assets-imgx.fubo.tv/marketing/lp/images/card/canada/CA_LP_Corus_card-2025.png?w=451',
    plan: 'entertainment',
    title: 'Entertainment',
    subtitle: 'Best for Families',
    listItems: (data) => [
      { available: true, text: `${data.channels}+ channels - 1000's of popular titles` },
      { available: false, text: 'Premier League matches' },
      {
        available: true,
        text: 'Lifestyle channels - Global TV, Slice & more',
      },

      { available: true, text: COMMON_FEATURES.CLOUD_DVR },
    ],
    ctaText: defaultCTA.canada.freeTrial.text,
    intervals: {
      monthly: {
        price: caPlanPricing.entertainment?.monthly?.price,
        discountedPrice: caPlanPricing.entertainment?.monthly?.discountedPrice,
        offerCode: ratePlanCodes.canada_ent_mo,
      },
      quarterly: {
        price: caPlanPricing.entertainment?.quarterly?.price,
        discountedPrice: caPlanPricing.entertainment?.quarterly?.discountedPrice,
        offerCode: ratePlanCodes.canada_ent_qt,
      },
    },
  },
  sports: {
    planType: 'sports',
    channelId: channelIds.ca_sports,
    promoImg: 'https://assets-imgx.fubo.tv/marketing/lp/images/card/ca_card_soccer-2.jpg?w=451',
    backgroundColor: '#ff3b04',
    plan: 'sports',
    title: 'Sports',
    subtitle: 'Best for Soccer and Sports Fans',
    listItems: (data) => [
      { available: true, text: `${data.channels} channels - Soccer and sports` },
      { available: true, text: 'All Premier League & Serie A matches' },
      { id: '4k', available: true, text: 'Includes Premier League in 4K' },
      { available: true, text: 'Bundesliga, Ligue 1 & CONCACAF' },
      { available: true, text: COMMON_FEATURES.CLOUD_DVR },
      {},
    ],
    intervals: {
      monthly: {
        price: caPlanPricing.sports?.monthly?.price,
        discountedPrice: caPlanPricing.sports?.monthly?.discountedPrice,
        offerCode: ratePlanCodes.canada_sports_mo,
      },
      quarterly: {
        price: caPlanPricing.sports?.quarterly?.price,
        discountedPrice: caPlanPricing.sports?.quarterly?.discountedPrice,
        savingsNote: '45% off',
        offerCode: ratePlanCodes.canada_sports_qt,
      },
      annual: {
        price: caPlanPricing.sports?.annual?.price,
        discountedPrice: caPlanPricing.sports?.annual?.discountedPrice,
        offerCode: ratePlanCodes.canada_sports_yr,
      },
    },
  },
  premium: {
    planType: 'premium',
    channelId: channelIds.ca_premium,
    promoImg:
      'https://assets-imgx.fubo.tv/marketing/lp/images/card/ca-premium-card-img-24.jpg?w=451',
    plan: 'premium',
    title: 'Premium',
    subtitle: 'Best for Everyone',
    listItems: (data) => [
      { available: true, text: `${data.channels}+ channels - Entertainment and sports` },
      { available: true, text: 'All Premier League & Serie A matches' },
      { available: true, text: 'Includes Premier League in 4K' },
      { available: true, text: 'Includes NBA TV' },
      {
        available: true,
        text: 'Lifestyle channels - Global TV, W Network & more',
      },

      { available: true, text: 'Unlimited Cloud DVR included' },
    ],
    intervals: {
      monthly: {
        price: caPlanPricing.premium.monthly.price,
        discountedPrice: caPlanPricing.premium.monthly.discountedPrice,
        offerCode: ratePlanCodes.canada_premium_mo,
      },
      quarterly: {
        price: caPlanPricing.premium.quarterly.price,
        discountedPrice: caPlanPricing.premium.quarterly.discountedPrice,
        offerCode: ratePlanCodes.canada_premium_qt,
      },
      annual: {
        price: caPlanPricing.premium.annual.price,
        discountedPrice: caPlanPricing.premium.annual.discountedPrice,
        offerCode: ratePlanCodes.canada_premium_yr,
      },
    },
  },
  cricket: {
    planType: 'cricket',
    promoImg: 'https://assets-imgx.fubo.tv/marketing/lp/images/card/canada/ca_card_cricket_2.jpg',
    plan: 'cricket_elite',
    title: (
      <>
        Cricket Elite <br />
      </>
    ),
    listItems: () => [
      { available: true, text: `100s of live cricket matches` },
      { available: true, text: 'Live Indian Premier League matches' },
      {
        available: true,
        text: 'Includes European Cricket Championship, Super Smash & more',
      },
      { available: true, text: 'Pakistan Super League & other leagues' },
      { available: true, text: 'ICC international events all year long' },
      { available: true, text: 'Unlimited Cloud DVR included' },
      { available: true, text: 'Watch on up to 3 screens' },
    ],
    ctaText: defaultCTA.canada.noTrial.text,
    intervals: {
      monthly: {
        price: caPlanPricing.cricket.monthly.price,
        discountedPrice: caPlanPricing.cricket.monthly.discountedPrice,
        offerCode: ratePlanCodes.canada_cricket_elite_mo,
      },
      quarterly: {
        price: caPlanPricing.cricket.quarterly.price,
        discountedPrice: caPlanPricing.cricket.quarterly.discountedPrice,
        offerCode: ratePlanCodes.canada_cricket_elite_qt,
      },
      annual: {
        price: caPlanPricing.cricket.annual.price,
        discountedPrice: caPlanPricing.cricket.annual.discountedPrice,
        offerCode: ratePlanCodes.canada_cricket_elite_yr,
      },
    },
  },
}

export const canadaPlanData = {
  entertainment: baseData.entertainment,
  sports: baseData.sports,
  cricket: baseData.cricket,
  premium: baseData.premium,
  cricket_w_sports_plus: {
    ...baseData.cricket,
    plan: 'cricket_w_sports_plus',
    title: 'Cricket with Sports Plus',
    promoImg: 'https://assets-imgx.fubo.tv/marketing/lp/images/card/canada/ca_card_cricket.jpg',
    listItems: () => modifyListItems(baseData.cricket.listItems(), { 2: false }),
    ctaText: defaultCTA.canada.noTrial.text,
    intervals: {
      monthly: {
        price: caPlanPricing.cricket_w_sports_plus.monthly.price,
        discountedPrice: caPlanPricing.cricket_w_sports_plus.monthly.discountedPrice,
        offerCode: ratePlanCodes.canada_cricket_mo,
      },
      quarterly: {
        price: caPlanPricing.cricket_w_sports_plus.quarterly.price,
        discountedPrice: caPlanPricing.cricket_w_sports_plus.quarterly.discountedPrice,
        offerCode: ratePlanCodes.canada_cricket_qt,
      },
      annual: {
        price: caPlanPricing.cricket_w_sports_plus.annual.price,
        discountedPrice: caPlanPricing.cricket_w_sports_plus.annual.discountedPrice,
        offerCode: ratePlanCodes.canada_cricket_yr,
      },
    },
  },
  soccer_offer: {
    ...baseData.sports,
    plan: 'soccer_offer',
    ctaText: defaultCTA.canada.noTrial.text,
    intervals: {
      monthly: {
        price: 29.99,
        discountedPrice: 24.99,
        savingsNote: '$5 off your first bill',
        offerCode: promoCodes.canada_soccer_mo,
      },
      quarterly: {
        price: 79.97,
        discountedPrice: 64.97,
        savingsNote: '$15 off your first bill',
        offerCode: promoCodes.canada_soccer_qt,
      },
      annual: {
        price: 279.99,
        discountedPrice: 259.99,
        savingsNote: '$20 off your first bill',
        offerCode: promoCodes.canada_soccer_yr,
      },
    },
  },
  ftf_offer: {
    ...baseData.sports,
    plan: 'ftf_offer',
    ctaText: defaultCTA.canada.noTrial.text,
    intervals: {
      ...baseData.sports.intervals,
      quarterly: {
        price: 79.97,
        discountedPrice: 49.97,
        savingsNote: '$30 off your first bill',
        offerCode: promoCodes.canada_ftf_canada,
      },
    },
  },
  super_channel_offer: {
    ...baseData.entertainment,
    title: 'Entertainment Plus',
    plan: 'super_channel_offer',
    imageOverlay: 'linear-gradient(186deg, rgba(4, 9, 17, .4) 15.04%, rgb(2, 9, 17) 65.16%)',
    listItems: (data) => [
      ...baseData.entertainment.listItems(data),
      { available: true, text: 'All Super Channel networks' },
    ],
    ctaText: defaultCTA.canada.freeTrial.text,
    intervals: {
      ...baseData.entertainment.intervals,
      monthly: {
        price: 24.98,
        offerCode: promoCodes.canada_entertainment_plus,
      },
      quarterly: {
        price: 69.99,
        offerCode: promoCodes.canada_entertainment_plus_quarterly,
      },
    },
  },
  mlbtv_offer: {
    ...baseData.sports,
    plan: 'mlbtv_offer',
    title: 'Sports & MLB.TV',
    subtitle: 'Best for Sports Fans',
    promoImg:
      'https://assets-imgx.fubo.tv/marketing/lp/images/card/canada/ca_card_mlb-tv.jpg?w=750&ch=width&auto=format,compress',
    listItems: (data) => {
      const items = baseData.sports.listItems(data)
      return [
        items[0],
        { available: true, text: 'MLB.TV, featuring 2,000+ Live Games per year' },
        ...items.slice(1),
      ]
    },
    ctaText: defaultCTA.canada.noTrial.text,
    intervals: {
      monthly: {
        price: 49.99,
        discountedPrice: 39.99,
        savingsNote: '$10 off for 2 months',
        offerCode: promoCodes.canada_mlbtv_mo,
      },
      quarterly: {
        price: 149.97,
        discountedPrice: 119.97,
        savingsNote: '$30 off for 2 quarters',
        offerCode: promoCodes.canada_mlbtv_qt,
      },
      annual: {
        price: 359.99,
        discountedPrice: 335.88,
        savingsNote: '$24 off the first year',
        offerCode: promoCodes.canada_mlbtv_yr,
      },
    },
  },
  sabres_offer: {
    ...baseData.sports,
    plan: 'sabres_offer',
    promoImg: 'https://assets-imgx.fubo.tv/marketing/lp/images/card/ca_card_buffalo_sabres.jpg',
    listItems: (data) => {
      const items = canadaPlanData.sports.listItems(data)
      return [{ available: true, text: 'LIVE Buffalo Sabres games' }, ...items]
    },
    ctaText: defaultCTA.canada.freeTrial.text,
    intervals: {
      monthly: {
        ...baseData.sports.intervals.monthly,
        discountedPrice: 12.49,
        savingsNote: '58% off for 12 months',
        offerCode: promoCodes.canada_sabres_mo,
      },
      quarterly: {
        ...baseData.sports.intervals.quarterly,
        discountedPrice: 34.99,
        savingsNote: '56% off for 12 months',
        offerCode: promoCodes.canada_sabres_qt,
      },
      annual: {
        ...baseData.sports.intervals.annual,
        discountedPrice: 129.99,
        savingsNote: '54% off for 12 months',
        offerCode: promoCodes.canada_sabres_yr,
      },
    },
  },
  mastercard_offer: {
    ...baseData.sports,
    plan: 'mastercard_offer',
    ctaText: defaultCTA.canada.noTrial.text,
    intervals: {
      quarterly: {
        ...baseData.sports.intervals.quarterly,
        discountedPrice: 53.97,
        savingsNote: '$30 off your first bill',
        offerCode: promoCodes.canada_mastercard,
      },
    },
  },
  mlbtv_standalone: {
    plan: 'mlbtv_standalone',
    title: 'MLB.TV',
    subtitle: 'Stream every out-of-market MLB game',
    promoImg: 'https://assets-imgx.fubo.tv/marketing/lp/images/card/canada/ca_card_mlb-tv-2.jpg',
    listItems: () => [
      { available: true, text: 'Every out-of-market MLB game' },
      { available: true, text: 'Unlimited Cloud DVR' },
      { available: true, text: '70 live TV channels included FREE' },
      { available: true, text: 'No contract' },
      { available: true, text: 'No Fubo base plan required' },
    ],
    networkLogos: [
      createNetworkLogo(networkLogos.mlbtv, 150),
      {
        alt: '+MORE',
      },
    ],
    ctaText: defaultCTA.canada.freeTrial.text,
    intervals: {
      monthly: {
        prePriceText: 'Only',
        price: 39.99,
        offerCode: ratePlanCodes.canada_mlb_tv_mo,
      },
      quarterly: null,
      annual: {
        prePriceText: 'Only',
        price: 239.9,
        offerCode: ratePlanCodes.canada_mlb_tv_yr,
        banner: 'Save 50%',
        asterisk: true,
      },
    },
  },
  sports_pro: {
    channelId: channelIds.ca_sports_pro,
    promoImg:
      'https://assets-imgx.fubo.tv/marketing/lp/images/card/canada/DAZN_Plancard_bg_3.jpg?w=600',
    plan: 'sports_pro',
    title: 'Sports Pro',
    subtitle: 'Every NFL game, every EPL match & more',
    backgroundColor: '#0004FF',
    listItems: (data) => [
      { available: true, text: `${data.channels}+ channels` },
      { available: true, text: 'Plus Champions League, Europa League & more on DAZN' },
      { available: true, text: 'All NFL games with Game Pass on DAZN' },
      { available: true, text: 'All Premier League & Serie A matches' },
      { id: '4k', available: true, text: 'Includes Premier League 4K' },
      { available: true, text: 'Bundesliga, Ligue 1 & CONCACAF' },
      { available: true, text: COMMON_FEATURES.CLOUD_DVR },
    ],
    ctaText: defaultCTA.canada.noTrial.text,
    intervals: {
      monthly: {
        offerCode: ratePlanCodes.canada_sports_pro_mo,
        price: 54.99,
        discountedPrice: 34.99,
        savingsNote: '$20 off',

        // footnote:
        //   '*With a 12 month commitment $34.99 for the first month, then $44.99 per month. With no commitment $54.99 per month. Prices subject to change. Additional taxes and fees may apply.',
      },
      quarterly: {
        price: 149.97,
        discountedPrice: 134.97,
        offerCode: ratePlanCodes.canada_sports_pro_qt,
        savingsNote: '$5 off per month',
      },
      annual: {
        price: 449.93,
        offerCode: ratePlanCodes.canada_sports_pro_yr,
      },
    },
  },
}

export const canadaPlanSet = {
  entertainment: [canadaPlanData.entertainment, canadaPlanData.premium],
  sports: [canadaPlanData.sports, canadaPlanData.sports_pro],
  cricket: [canadaPlanData.cricket_w_sports_plus, canadaPlanData.cricket],
  premium: [canadaPlanData.premium],
}

export const CALowestSportsPrice = calculateLowestPrice(canadaPlanSet.sports)

export const canadaSports_EPL_UCL_Override = {
  sports: {
    all: {
      promoImg: null,
      subtitle: null,
      networkLogosTop: [
        {
          logo: 'https://assets-imgx.fubo.tv/marketing/lp/images/card/canada/logos/FSN-r-v2.png',
          alt: 'Fubo Sports Network',
        },
        {
          logo: 'https://assets-imgx.fubo.tv/marketing/lp/images/card/canada/logos/EPL-r-v2.png',
          alt: 'Premier League',
        },
        {
          logo: 'https://assets-imgx.fubo.tv/marketing/lp/images/card/canada/logos/Serie-a-r-v2.png',
          alt: 'Serie A',
        },
      ],
    },
  },
  sports_pro: {
    all: {
      promoImg: null,
      subtitle: null,
      networkLogosTop: [
        {
          logo: 'https://assets-imgx.fubo.tv/marketing/lp/images/card/canada/logos/DAZN-b-v2.png',
          alt: 'DAZN',
        },
        {
          logo: 'https://assets-imgx.fubo.tv/marketing/lp/images/card/canada/logos/UCL-b-v2.png',
          alt: 'UCL',
        },
        {
          alt: '+',
        },
        {
          logo: 'https://assets-imgx.fubo.tv/marketing/lp/images/card/canada/logos/FSN-b-v2.png',
          alt: 'Fubo Sports Network',
        },
        {
          logo: 'https://assets-imgx.fubo.tv/marketing/lp/images/card/canada/logos/EPL-b-v2.png',
          alt: 'Premier League',
        },
        {
          logo: 'https://assets-imgx.fubo.tv/marketing/lp/images/card/canada/logos/Serie-a-b-v2.png',
          alt: 'Serie A',
        },
      ],
    },
  },
}
